import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { COLORS } from 'theme/colors';

export interface FaqItemType {
  title: string;
  perex: string;
}

type FaqItemProps = FaqItemType;

const AccordionStyled = styled(Accordion)(
  ({ theme: { spacing } }) => ({
    marginBottom: spacing(1.5),
    borderRadius: spacing(1),
    ':first-of-type': {
      borderRadius: spacing(1),
    },
    ':before': {
      display: 'none',
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      alignSelf: 'flex-start',
      position: 'relative',
      top: spacing(1.5),
    },
    '&.Mui-expanded': {
      marginBottom: spacing(3),
      marginTop: spacing(3),
    },
  }),
);

const AccordionSummaryStyled = styled(AccordionSummary)(
  ({ theme: { spacing } }) => ({
    padding: spacing(1, 3),
    '& .MuiAccordionSummary-expandIconWrapper': {
      alignSelf: 'flex-start',
      position: 'relative',
      top: spacing(1.5),
    },
  }),
);

const AccordionDetailsStyled = styled(AccordionDetails)(
  ({ theme: { spacing, breakpoints } }) => ({
    padding: spacing(0, 3, 5, 3),
    [breakpoints.down('md')]: {
      paddingBottom: spacing(4),
    },
  }),
);

const FaqItem: React.FC<FaqItemProps> = ({ title, perex }) => {
  const [expanded, setExpanded] = useState(false);
  const { spacing } = useTheme();

  return (
    <AccordionStyled
      expanded={expanded}
      // Using onChange (not onClick), so that click on perex doesn't collapse the answer
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      elevation={0}
      square
    >
      <AccordionSummaryStyled
        expandIcon={
          expanded ? (
            <RemoveIcon color={'secondary'} sx={{ fontSize: 20 }} />
          ) : (
            <AddIcon sx={{ color: COLORS.GREY.MAIN, fontSize: 20 }} />
          )
        }
      >
        <Typography
          component="span"
          color={expanded ? COLORS.DARK_BLUE.MAIN : COLORS.GREY.MAIN}
          fontSize="0.875rem"
          fontWeight={700}
          sx={{
            marginTop: spacing(expanded ? 2 : 0),
          }}
        >
          {title}
        </Typography>
      </AccordionSummaryStyled>
      <AccordionDetailsStyled>
        {/* Using dangerouslySetInnerHTML, since we trust the source (our textations) */}
        <Typography
          component="div"
          fontSize="0.875rem"
          color={COLORS.GREY.MAIN}
          whiteSpace="pre-line"
          dangerouslySetInnerHTML={{ __html: perex }}
        />
      </AccordionDetailsStyled>
    </AccordionStyled>
  );
};

export default FaqItem;
